import type { Dispatch, SetStateAction } from "react";
import { useRef } from "react";
import { useLocalization } from "../../../stores/localizationContext";
import { useLiveSessionStore } from "../../../stores/useLiveSessionStore";
import { Dropdown } from "../../Common/Dropdown/Dropdown";

// Centralized pen colors definition
// eslint-disable-next-line react-refresh/only-export-components
export const penColors: Record<string, string> = {
  Black: "#4d4d4f",
  Blue: "#56a5e1",
  Red: "#e24c3c",
  Green: "#119f25",
  Orange: "#f4a304",
  Purple: "#ff2aff",
};

// Pen color keys list (used for rendering)
// eslint-disable-next-line react-refresh/only-export-components
export const penColorKeys = Object.keys(penColors);

// Map thickness names to numeric stroke widths for the engine
// eslint-disable-next-line react-refresh/only-export-components
export const thicknessToStrokeWidth: Record<string, number> = {
  thin: 1,
  regular: 2,
  medium: 3,
  semibold: 4,
  bold: 5,
};

// Thickness keys list (used for rendering)
// eslint-disable-next-line react-refresh/only-export-components
export const thicknessKeys = Object.keys(thicknessToStrokeWidth);

interface ToolbarPensProps {
  isObjectEraser: boolean;
  setIsObjectEraser: Dispatch<SetStateAction<boolean>>;
  dropdownRerenderKey: number;
  setDropdownRerenderKey: Dispatch<SetStateAction<number>>;
}

export const ToolbarPens = ({
  isObjectEraser,
  setIsObjectEraser,
  dropdownRerenderKey,
  setDropdownRerenderKey,
}: ToolbarPensProps) => {
  console.log("[ToolbarPens] Component rendered");

  // Localization
  const { t } = useLocalization();

  // State from Zustand (pure state, no side effects)
  const activePen = useLiveSessionStore((state) => state.activePen);
  const setActivePen = useLiveSessionStore((state) => state.setActivePen);
  const openDropdown = useLiveSessionStore((state) => state.openDropdown);
  const setOpenDropdown = useLiveSessionStore((state) => state.setOpenDropdown);

  // Engine communication (side effect)
  const sendMessageToEngine = useLiveSessionStore(
    (state) => state.sendMessageToEngine
  );

  // Use centralized color list + eraser
  const colors = [...penColorKeys, "Eraser"];

  return (
    <div className="toolbar__pens">
      {colors.map((color) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const btnRef = useRef<HTMLButtonElement>(null);
        return (
          <div
            key={color}
            style={{ position: "relative", display: "inline-block" }}
          >
            <button
              ref={btnRef}
              className={`btn btn-pen${activePen === color ? " active" : ""}`}
              onClick={() => {
                console.log("[ToolbarPens] Pen clicked:", color);

                if (activePen !== color) {
                  // Update active pen in Zustand store
                  setActivePen(color);

                  // Set default thickness if not exists (first option: "thin")
                  // But don't send to engine until user explicitly selects it
                  if (!localStorage.getItem(`${color}Thickness`)) {
                    localStorage.setItem(`${color}Thickness`, "thin");
                  }

                  // If object eraser is enabled, disable it and switch tool type
                  if (isObjectEraser) {
                    console.log("[ToolbarPens] Exiting DeletePart mode");
                    setIsObjectEraser(false);
                    localStorage.setItem("isObjectEraser", "false");

                    // Exit DeletePart mode by switching to appropriate tool type
                    if (color === "Eraser") {
                      console.log(
                        "[ToolbarPens] Switching from DeletePart to Eraser"
                      );
                      sendMessageToEngine("changeTool", { type: "Eraser" });
                    } else {
                      console.log(
                        "[ToolbarPens] Switching from DeletePart to Draw"
                      );
                      sendMessageToEngine("changeTool", { type: "Draw" });
                    }
                  }

                  // Only send color change to engine via Zustand action, NOT thickness
                  // Thickness is sent only when user clicks dropdown option
                  // Send to engine (side effect in component)
                  if (color === "Eraser") {
                    // Eraser uses white color
                    console.log(
                      "[ToolbarPens] Eraser selected - sending white color to engine"
                    );
                    sendMessageToEngine("changeTool", { color: "#FFFFFF" });
                  } else {
                    const colorHex = penColors[color];
                    console.log(
                      "[ToolbarPens] Sending only color to engine:",
                      colorHex
                    );
                    sendMessageToEngine("changeTool", { color: colorHex });
                  }
                }

                // If this dropdown is already open, close it
                if (openDropdown === color) {
                  setOpenDropdown("");
                }
              }}
              onDoubleClick={() => setOpenDropdown(color)}
            >
              <figure>
                <img
                  src={`/assets/images/pen${color}.svg`}
                  alt={t(color)}
                />
              </figure>
            </button>
            <Dropdown
              open={openDropdown === color}
              anchorRef={btnRef}
              onClose={() => setOpenDropdown("")}
              className="dropdown-menu"
              offsetY={-20}
            >
              <h4 className="dropdown-title">
                {color === "Eraser" ? t("erasers") : t("thickness")}
              </h4>
              <ul key={`${color}-${dropdownRerenderKey}`}>
                {thicknessKeys.map((id) => {
                  const isActive =
                    localStorage.getItem(`${color}Thickness`) === id;
                  return (
                    <li key={id}>
                      <button
                        className={`btn${isActive ? " active" : ""}`}
                        onClick={() => {
                          console.log(
                            "[ToolbarPens] Thickness clicked:",
                            id,
                            "for color:",
                            color
                          );

                          // If object eraser is enabled, disable it and switch tool type
                          if (isObjectEraser) {
                            console.log(
                              "[ToolbarPens] Exiting DeletePart mode"
                            );
                            setIsObjectEraser(false);
                            localStorage.setItem("isObjectEraser", "false");

                            // Exit DeletePart mode by switching to appropriate tool type
                            if (color === "Eraser") {
                              console.log(
                                "[ToolbarPens] Switching from DeletePart to Eraser"
                              );
                              sendMessageToEngine("changeTool", {
                                type: "Eraser",
                              });
                            } else {
                              console.log(
                                "[ToolbarPens] Switching from DeletePart to Draw"
                              );
                              sendMessageToEngine("changeTool", {
                                type: "Draw",
                              });
                            }
                          }

                          // Save to localStorage
                          localStorage.setItem(`${color}Thickness`, id);
                          // Force dropdown re-render
                          setDropdownRerenderKey((k) => k + 1);
                          // Send strokeWidth to engine (side effect in component)
                          const strokeWidth = thicknessToStrokeWidth[id];
                          console.log(
                            "[ToolbarPens] Sending strokeWidth to engine:",
                            strokeWidth
                          );
                          sendMessageToEngine("changeTool", { strokeWidth });
                        }}
                        aria-label={t(`select_${id}_thickness`)}
                        role="option"
                        aria-selected={isActive}
                      >
                        <div
                          className={`line line--${id}${
                            isActive ? " active-line" : ""
                          } line--${color.toLowerCase()}`}
                          style={
                            !isActive
                              ? { backgroundColor: "#d8d8d8" }
                              : undefined
                          }
                        ></div>
                      </button>
                    </li>
                  );
                })}
              </ul>
              {color === "Eraser" && (
                <div className="toggle-switch">
                  <span className="toggle-switch__label">
                    {t("object_eraser")}
                  </span>
                  <label className="toggle-switch__control">
                    <input
                      type="checkbox"
                      checked={isObjectEraser}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        console.log(
                          "[ToolbarPens] Object Eraser toggled:",
                          isChecked
                        );

                        // Update local state
                        setIsObjectEraser(isChecked);
                        localStorage.setItem(
                          "isObjectEraser",
                          isChecked.toString()
                        );

                        // Send to engine (side effect in component)
                        if (isChecked) {
                          console.log(
                            "[ToolbarPens] Switching to DeletePart tool"
                          );
                          sendMessageToEngine("changeTool", {
                            type: "DeletePart",
                          });
                        } else {
                          // When unchecked, exit DeletePart and revert to normal eraser
                          console.log(
                            "[ToolbarPens] Exiting DeletePart mode, switching to Eraser"
                          );
                          sendMessageToEngine("changeTool", { type: "Eraser" });
                          console.log(
                            "[ToolbarPens] Setting eraser color to white"
                          );
                          sendMessageToEngine("changeTool", {
                            color: "#FFFFFF",
                          });
                        }
                      }}
                      aria-label={t("toggle_object_eraser")}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              )}
            </Dropdown>
          </div>
        );
      })}
    </div>
  );
};
